import { captureException } from "@/lib/sentry";
const STORAGE_KEY = "mm_attribution";

export interface Attribution {
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  utmTerm?: string | null;
  utmContent?: string | null;
  referrer?: string | null;
  landingPage?: string | null;
  gclid?: string | null;
  fbclid?: string | null;
  msclkid?: string | null;
  wbraid?: string | null;
  gbraid?: string | null;
  utmId?: string | null;
  subscriber_id?: string | null;
}

function captureAttribution(): void {
  if (typeof window === "undefined") return;

  try {
    const existing = localStorage.getItem(STORAGE_KEY);
    if (existing) return;
  } catch {
    // intentionally silent
    return;
  }

  const params = new URLSearchParams(window.location.search);
  const data: Attribution = {
    utmSource: params.get("utm_source") || null,
    utmMedium: params.get("utm_medium") || null,
    utmCampaign: params.get("utm_campaign") || null,
    utmTerm: params.get("utm_term") || null,
    utmContent: params.get("utm_content") || null,
    referrer: document.referrer || null,
    landingPage: window.location.pathname + window.location.search,
    gclid: params.get("gclid") || null,
    fbclid: params.get("fbclid") || null,
    msclkid: params.get("msclkid") || null,
    wbraid: params.get("wbraid") || null,
    gbraid: params.get("gbraid") || null,
    utmId: params.get("utm_id") || null,
    subscriber_id: params.get("uid") || null,
  };

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (_swallowedErr) { captureException(_swallowedErr); }
}

export function getAttribution(): Attribution {
  if (typeof window === "undefined") return {};
  captureAttribution();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    // intentionally silent
    return {};
  }
}

/**
 * Reads the _fbp cookie set by Meta's pixel script.
 * This is read fresh at submit time (not stored in localStorage) because
 * the pixel may not have initialized yet when the page first loaded.
 * Returns null if the cookie is absent.
 */
export function getFbp(): string | null {
  if (typeof document === "undefined") return null;
  try {
    const match = document.cookie.match(/(?:^|;\s*)_fbp=([^;]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    // intentionally silent
    return null;
  }
}

export function clearAttribution(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (_swallowedErr) { captureException(_swallowedErr); }
}

export function initAttribution(): void {
  captureAttribution();
}
